// React Query
import { useMutation, UseMutationResult } from "@tanstack/react-query";

// API
import { loginProvider } from "@Settings/api/AdministrationTools/mutations";

// Helpers
import { errorLogger } from "@Common/helpers/logger";

// Types
import { CustomCredentials, TSelectedProvider } from "@Settings/types/administrationTools/utils";

type UseLoginProviderProps<TData> = Readonly<{
	mutationKey?: string[];
	integrationProviderProperty: string;
	locationId: string;
	provider: TSelectedProvider;
	// Callbacks
	onSuccess?: (result: TData) => void;
}>;

function useLoginProvider<TData, TVariables extends CustomCredentials = CustomCredentials>({
	locationId,
	integrationProviderProperty,
	provider,
	mutationKey = ["loginProvider"],
	// Callbacks
	onSuccess,
}: UseLoginProviderProps<TData>): UseMutationResult<TData, Error, TVariables> {
	return useMutation<TData, Error, TVariables>({
		mutationKey,
		mutationFn: async (input: TVariables) => {
			const result = await loginProvider<TData>({
				locationId,
				provider,
				integrationProviderProperty,
				credentials: input,
			});
			if (result === null) {
				errorLogger("Login failed");
				return Promise.reject(new Error("Login failed"));
			}
			return result;
		},
		onSuccess: (result) => {
			onSuccess?.(result);
		},
	});
}

export default useLoginProvider;
